import type { LogEntry, LogQuery } from "@skaile/workspaces/types";
import { createTRPCUntypedClient, httpSubscriptionLink } from "@trpc/client";

export interface RemoteTailOptions {
  url: string;
  token?: string;
  projectId: string;
  sessionId: string;
  /**
   * Same shape as the history `LogQuery`. The platform's `onLogEntry`
   * subscription does NOT filter — filters are applied client-side here so
   * the user sees a consistent view between history and tail.
   */
  query: Omit<LogQuery, "sessionId">;
  signal?: AbortSignal;
  onEntry: (entry: LogEntry) => void;
}

/**
 * Subscribe to the platform's `workspace.onLogEntry` SSE stream and forward
 * each entry (after client-side filtering) to `onEntry`. Resolves cleanly
 * when `signal` is aborted.
 */
export async function remoteTail(opts: RemoteTailOptions): Promise<void> {
  const trpcUrl = normalizeTrpcUrl(opts.url);
  const client = createTRPCUntypedClient({
    links: [
      httpSubscriptionLink({
        url: trpcUrl,
        eventSourceOptions: () => ({
          // EventSource init dict; Bun ignores unknown fields.
          ...(opts.token ? { headers: { authorization: `Bearer ${opts.token}` } } : {}),
        }),
      }),
    ],
  });

  const filter = makeFilter(opts.query);

  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (err?: unknown) => {
      if (settled) return;
      settled = true;
      try {
        sub.unsubscribe();
      } catch {
        // best-effort
      }
      opts.signal?.removeEventListener("abort", onAbort);
      if (err) reject(err);
      else resolve();
    };

    const onAbort = () => finish();
    if (opts.signal?.aborted) {
      // Avoid subscribing if already aborted.
      resolve();
      return;
    }
    opts.signal?.addEventListener("abort", onAbort, { once: true });

    const sub = client.subscription(
      "workspace.onLogEntry",
      { projectId: opts.projectId, sessionId: opts.sessionId },
      {
        onData: (raw) => {
          const entry = raw as LogEntry;
          if (filter(entry)) opts.onEntry(entry);
        },
        onError: (err) => finish(err),
        onComplete: () => finish(),
        onStopped: () => finish(),
      },
    );
  });
}

function normalizeTrpcUrl(url: string): string {
  const trimmed = url.replace(/\/+$/, "");
  if (trimmed.endsWith("/trpc")) return trimmed;
  return `${trimmed}/trpc`;
}

/**
 * Build a client-side predicate matching `local-query`'s SQL filters.
 * Applied after each yielded entry from the SSE stream because the platform
 * route does not pre-filter.
 */
function makeFilter(q: Omit<LogQuery, "sessionId">): (e: LogEntry) => boolean {
  const sinceMs = q.since ? Date.parse(q.since) : undefined;
  const untilMs = q.until ? Date.parse(q.until) : undefined;
  const searchLower = q.search ? q.search.toLowerCase() : undefined;
  const levelSet = q.levels && q.levels.length > 0 ? new Set(q.levels) : undefined;
  const kind = q.source?.kind;
  const subkind = q.source?.subkind;
  const instance = q.source?.instance;

  return (e) => {
    if (kind && e.source.kind !== kind) return false;
    if (subkind && e.source.subkind !== subkind) return false;
    if (instance && e.source.instance !== instance) return false;
    if (levelSet && !levelSet.has(e.level)) return false;
    if (sinceMs !== undefined) {
      const ts = Date.parse(e.timestamp);
      if (!Number.isNaN(ts) && ts < sinceMs) return false;
    }
    if (untilMs !== undefined) {
      const ts = Date.parse(e.timestamp);
      if (!Number.isNaN(ts) && ts > untilMs) return false;
    }
    if (searchLower && !e.message.toLowerCase().includes(searchLower)) return false;
    return true;
  };
}
